/**
 * v0M4.0k.3 (2026-07-27) — Combat.xlsx typed loader.
 *
 * Loads two attacker × defender matrices for melee + ranged to-hit
 * deltas from `src/data/generated/combat.json` (built by
 * `scripts/convert-xlsx.ts` from `docs/Combat.xlsx`).
 *
 * Values are **additive percentage-point deltas** — e.g. -0.20 means
 * -20 pp on the base to-hit chance. Consumed by
 * `battle-terrain-modifiers.ts` at v0M4.0k.4 when the hardcoded
 * matrices are retired.
 *
 * Xlsx labels are lowercase (`plains / desert / forest / swamp / hills /
 * bridge / wall / gate`). Consumers must resolve TerrainType enum to
 * lowercase label via `terrainTypeToXlsxLabel` before matrix lookup.
 */

import combatBlob from './generated/combat.json' with { type: 'json' };
import type { TerrainType } from '../sim/battle-state';

interface CombatBlob {
  readonly schemaVersion: number;
  readonly melee: Readonly<Record<string, Readonly<Record<string, number>>>>;
  readonly ranged: Readonly<Record<string, Readonly<Record<string, number>>>>;
  readonly rangedIsMirrorOfMelee: boolean;
}

const COMBAT: CombatBlob = combatBlob as unknown as CombatBlob;

/**
 * TerrainType enum → Combat.xlsx label. Xlsx uses lowercase-natural
 * names; code enum uses PascalCase. Water + Mountains have no xlsx
 * entry (cohorts don't stand on impassable terrain) — return null so
 * consumers fall back to 0 delta.
 */
export function terrainTypeToXlsxLabel(t: TerrainType): string | null {
  switch (t) {
    case 'Plains': return 'plains';
    case 'Desert': return 'desert';
    case 'Forest': return 'forest';
    case 'Swamp':  return 'swamp';
    case 'Hills':  return 'hills';
    case 'Bridge': return 'bridge';
    case 'Wall':   return 'wall';
    case 'Gate':   return 'gate';
    // Water + Mountains: no xlsx entry (impassable to cohorts).
    case 'Water':
    case 'Mountains':
    default: return null;
  }
}

/**
 * Melee to-hit additive delta for (attacker → defender) terrain pair.
 * Blank matrix cells + unknown pairs return 0 (no modifier).
 */
export function meleeTerrainToHitDelta(attacker: TerrainType, defender: TerrainType): number {
  const atk = terrainTypeToXlsxLabel(attacker);
  const def = terrainTypeToXlsxLabel(defender);
  if (atk === null || def === null) return 0;
  const row = COMBAT.melee[atk];
  if (!row) return 0;
  const v = row[def];
  return typeof v === 'number' ? v : 0;
}

/**
 * Ranged to-hit additive delta for (attacker → defender) terrain pair.
 * Falls back to melee when Combat_RangedToHitModifiers sheet not
 * authored yet (`rangedIsMirrorOfMelee=true`).
 */
export function rangedTerrainToHitDelta(attacker: TerrainType, defender: TerrainType): number {
  const atk = terrainTypeToXlsxLabel(attacker);
  const def = terrainTypeToXlsxLabel(defender);
  if (atk === null || def === null) return 0;
  const row = COMBAT.ranged[atk];
  if (!row) return 0;
  const v = row[def];
  return typeof v === 'number' ? v : 0;
}

/** True when the ranged matrix is a mirror of melee (sheet not yet
 *  authored by Michael). Consumers can surface a dev warning if desired. */
export function rangedIsMirrorOfMelee(): boolean {
  return COMBAT.rangedIsMirrorOfMelee;
}
